import React, { useState, useRef } from 'react';
import { KeywordWithIntent, KeywordIntent, ViewType } from '../types';
import { generateSampleExcel } from '../utils/generateSampleExcel';
import { getIntentColor, getIntentLabel } from '../utils/intentCategories';
import type { IntentCategoryConfig } from '../types';

interface FileUploadProps {
  onDataProcessed: (
    data: ProcessedKeywordData[], 
    intentCategories: string[], 
    intentCategoryConfig: Record<string, IntentCategoryConfig>, 
    availableViewTypes: ViewType[],
    fileInfo: { name: string; size: number; data: string }
  ) => void;
  onClose: () => void;
}

interface ProcessedKeywordData {
  productName?: string;
  productCategory: string;
  keywords: KeywordWithIntent[];
  ingredients?: string;
  legalClaims?: string;
  copy?: string;
  brand?: string;
  subBrand?: string;
}

interface ValidationError {
  row: number;
  column: string;
  message: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ onDataProcessed, onClose }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [previewData, setPreviewData] = useState<ProcessedKeywordData[]>([]);
  const [intentCategories, setIntentCategories] = useState<string[]>([]);
  const [intentCategoryConfig, setIntentCategoryConfig] = useState<Record<string, IntentCategoryConfig>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [availableViewTypes, setAvailableViewTypes] = useState<ViewType[]>([]);
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number; data: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const processFile = async (file: File) => {
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      alert('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setIsProcessing(true);
    setValidationErrors([]);

    try {
      // Import XLSX dynamically to avoid SSR issues
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      const processedData = processExcelData(jsonData);
      setPreviewData(processedData.data);
      setValidationErrors(processedData.errors);
      setIntentCategories(processedData.intentCategories);
      setIntentCategoryConfig(processedData.intentCategoryConfig);
      setAvailableViewTypes(processedData.availableViewTypes);
      
      // Store file information with base64 encoded data
      const base64Data = btoa(
        new Uint8Array(data).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      setFileInfo({
        name: file.name,
        size: file.size,
        data: base64Data
      });
      
      setShowPreview(true);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please check the file format.');
    } finally {
      setIsProcessing(false);
    }
  };

  const processExcelData = (data: any[][]): { 
    data: ProcessedKeywordData[], 
    errors: ValidationError[],
    intentCategories: string[],
    intentCategoryConfig: Record<string, IntentCategoryConfig>,
    availableViewTypes: ViewType[]
  } => {
    const errors: ValidationError[] = [];
    const intentCategorySet = new Set<string>();
    
    if (data.length < 2) {
      errors.push({ row: 0, column: 'all', message: 'File must have at least a header row and one data row' });
      return { data: [], errors, intentCategories: [], intentCategoryConfig: {}, availableViewTypes: [] };
    }

    // Helper function to normalize column names (lowercase and trim spaces)
    const normalizeColumnName = (name: string): string => {
      return String(name || '').toLowerCase().trim();
    };

    // Helper function to find column index by normalized name
    const findColumnIndex = (headers: string[], targetColumn: string): number => {
      const normalizedTarget = normalizeColumnName(targetColumn);
      return headers.findIndex(header => normalizeColumnName(header) === normalizedTarget);
    };

    // Helper function to parse boolean values from Excel
    const parseBoolean = (value: any): boolean | undefined => {
      if (value === undefined || value === null || value === '') return undefined;
      const strValue = String(value).toLowerCase().trim();
      if (strValue === 'true' || strValue === '1' || strValue === 'yes') return true;
      if (strValue === 'false' || strValue === '0' || strValue === 'no') return false;
      return undefined;
    };

    const headers = data[0] as string[];
    const requiredColumns = ['Keyword', 'Volume'];
    
    // Validate headers - check if all required columns exist (case-insensitive, ignoring spaces)
    const missingColumns = requiredColumns.filter(col => findColumnIndex(headers, col) === -1);
    if (missingColumns.length > 0) {
      errors.push({ row: 0, column: 'headers', message: `Missing required columns: ${missingColumns.join(', ')}` });
      return { data: [], errors, intentCategories: [], intentCategoryConfig: {}, availableViewTypes: [] };
    }

    // Get column indices using case-insensitive matching
    const productNameIndex = findColumnIndex(headers, 'Product Name');
    const brandIndex = findColumnIndex(headers, 'Brand');
    const subBrandIndex = findColumnIndex(headers, 'Sub-Brand');
    const productCategoryIndex = findColumnIndex(headers, 'Product Category');
    const keywordIndex = findColumnIndex(headers, 'Keyword');
    const intentIndex = findColumnIndex(headers, 'Intent Category');
    const volumeIndex = findColumnIndex(headers, 'Volume');
    
    // Determine which view types are available based on columns present
    const availableViewTypes: ViewType[] = [];
    if (productNameIndex !== -1) availableViewTypes.push('product');
    if (brandIndex !== -1) availableViewTypes.push('brand');
    if (subBrandIndex !== -1) availableViewTypes.push('sub-brand');
    
    // At least one grouping column must be present
    if (availableViewTypes.length === 0) {
      errors.push({ row: 0, column: 'headers', message: 'At least one of Product Name, Brand, or Sub-Brand columns must be present' });
      return { data: [], errors, intentCategories: [], intentCategoryConfig: {}, availableViewTypes: [] };
    }
    // Optional columns - product metadata
    const ingredientsIndex = findColumnIndex(headers, 'Ingredients');
    const legalClaimsIndex = findColumnIndex(headers, 'Legal Claims');
    const copyIndex = findColumnIndex(headers, 'Copy');
    // Optional columns - retailer availability
    const amazonIndex = findColumnIndex(headers, 'Amazon');
    const targetIndex = findColumnIndex(headers, 'Target');
    const walmartIndex = findColumnIndex(headers, 'Walmart');
    const krogerIndex = findColumnIndex(headers, 'Kroger');
    const instacartIndex = findColumnIndex(headers, 'Instacart');
    // Optional columns - other keyword metadata
    const dsFilterPassedIndex = findColumnIndex(headers, 'ds_filter_passed');
    const researchTypeIndex = findColumnIndex(headers, 'research_type');
    const intentScoreIndex = findColumnIndex(headers, 'intent_score');
    const keywordEngIndex = findColumnIndex(headers, 'keyword_eng');

    // Group data by a composite key (product name, brand, sub-brand)
    const productMap = new Map<string, ProcessedKeywordData>();

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const rowNum = i + 1;

      // Get values from optional grouping columns
      const productName = productNameIndex !== -1 && row[productNameIndex] ? String(row[productNameIndex]).trim() : undefined;
      const brand = brandIndex !== -1 && row[brandIndex] ? String(row[brandIndex]).trim() : undefined;
      const subBrand = subBrandIndex !== -1 && row[subBrandIndex] ? String(row[subBrandIndex]).trim() : undefined;
      
      // Skip empty rows - at least one grouping field and keyword must be present
      const hasGroupingField = productName || brand || subBrand;
      const keywordText = row[keywordIndex] ? String(row[keywordIndex]).trim() : '';
      
      if (!hasGroupingField || !keywordText) continue;

      const productCategory = String(row[productCategoryIndex] || '').trim();
      const intentCategory = String(row[intentIndex] || '').trim();
      const volumeValue = row[volumeIndex];
      // Optional columns - product metadata
      const ingredients = ingredientsIndex !== -1 && row[ingredientsIndex] ? String(row[ingredientsIndex]).trim() : undefined;
      const legalClaims = legalClaimsIndex !== -1 && row[legalClaimsIndex] ? String(row[legalClaimsIndex]).trim() : undefined;
      const copy = copyIndex !== -1 && row[copyIndex] ? String(row[copyIndex]).trim() : undefined;
      // Optional columns - retailer availability
      const amazon = amazonIndex !== -1 ? parseBoolean(row[amazonIndex]) : undefined;
      const target = targetIndex !== -1 ? parseBoolean(row[targetIndex]) : undefined;
      const walmart = walmartIndex !== -1 ? parseBoolean(row[walmartIndex]) : undefined;
      const kroger = krogerIndex !== -1 ? parseBoolean(row[krogerIndex]) : undefined;
      const instacart = instacartIndex !== -1 ? parseBoolean(row[instacartIndex]) : undefined;
      // Optional columns - other keyword metadata
      const ds_filter_passed = dsFilterPassedIndex !== -1 ? parseBoolean(row[dsFilterPassedIndex]) : undefined;
      const research_type = researchTypeIndex !== -1 && row[researchTypeIndex] ? String(row[researchTypeIndex]).trim() : undefined;
      const intent_score = intentScoreIndex !== -1 && row[intentScoreIndex] ? Number(row[intentScoreIndex]) : undefined;
      const keyword_eng = keywordEngIndex !== -1 && row[keywordEngIndex] ? String(row[keywordEngIndex]).trim() : undefined;

      // Use default intent category if not provided
      const finalIntentCategory = intentCategory || 'General';

      // Collect unique intent categories
      intentCategorySet.add(finalIntentCategory);

      // Validate volume
      let volume = 0; // default
      if (volumeValue !== undefined && volumeValue !== null && volumeValue !== '') {
        const parsedVolume = Number(volumeValue);
        if (isNaN(parsedVolume) || parsedVolume < 0 || parsedVolume > 1000000) {
          errors.push({ 
            row: rowNum, 
            column: 'Volume', 
            message: 'Volume must be a number between 0 and 1,000,000' 
          });
          continue;
        }
        volume = parsedVolume;
      }

      // Create a composite key for grouping (normalize to lowercase to prevent case-sensitivity issues)
      const compositeKey = `${(productName || 'N/A').toLowerCase()}|${(brand || 'N/A').toLowerCase()}|${(subBrand || 'N/A').toLowerCase()}`;

      // Get or create product entry
      if (!productMap.has(compositeKey)) {
        productMap.set(compositeKey, {
          productName,
          brand,
          subBrand,
          productCategory: productCategory || 'General',
          keywords: [],
          ingredients: ingredients || undefined,
          legalClaims: legalClaims || undefined,
          copy: copy || undefined
        });
      } else {
        // If product already exists, update ingredients/legalClaims/copy if they weren't set yet
        const existingProduct = productMap.get(compositeKey)!;
        if (!existingProduct.ingredients && ingredients) {
          existingProduct.ingredients = ingredients;
        }
        if (!existingProduct.legalClaims && legalClaims) {
          existingProduct.legalClaims = legalClaims;
        }
        if (!existingProduct.copy && copy) {
          existingProduct.copy = copy;
        }
      }

      const product = productMap.get(compositeKey)!;
      
      // Build keyword object with all available data
      const keyword: KeywordWithIntent = {
        text: keywordText,
        intent: finalIntentCategory as KeywordIntent,
        volume: volume
      };
      
      // Add optional fields if they exist
      if (amazon !== undefined) keyword.amazon = amazon;
      if (target !== undefined) keyword.target = target;
      if (walmart !== undefined) keyword.walmart = walmart;
      if (kroger !== undefined) keyword.kroger = kroger;
      if (instacart !== undefined) keyword.instacart = instacart;
      if (ds_filter_passed !== undefined) keyword.ds_filter_passed = ds_filter_passed;
      if (research_type !== undefined) keyword.research_type = research_type;
      if (intent_score !== undefined && !isNaN(intent_score)) keyword.intent_score = Math.round(intent_score * 100) / 100;
      if (keyword_eng !== undefined) keyword.keyword_eng = keyword_eng;
      
      product.keywords.push(keyword);
    }

    // Generate intent category config
    const intentCategories = Array.from(intentCategorySet).sort();
    const intentCategoryConfig: Record<string, IntentCategoryConfig> = {};
    intentCategories.forEach(category => {
      intentCategoryConfig[category] = {
        label: getIntentLabel(category),
        color: getIntentColor(category),
      };
    });

    return { 
      data: Array.from(productMap.values()), 
      errors,
      intentCategories,
      intentCategoryConfig,
      availableViewTypes
    };
  };

  const handleImport = () => {
    if (validationErrors.length === 0 && fileInfo) {
      onDataProcessed(previewData, intentCategories, intentCategoryConfig, availableViewTypes, fileInfo);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary-900">Import Keywords from Excel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {!showPreview ? (
          <div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors mb-6 ${
                isDragging
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isProcessing ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500 mb-4"></div>
                  <p className="text-gray-600">Processing file...</p>
                </div>
              ) : (
                <div>
                  <div className="text-4xl text-gray-400 mb-4">📁</div>
                  <p className="text-lg text-gray-600 mb-2">
                    Drag and drop your Excel file here, or click to browse
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Supports .xlsx and .xls files
                  </p>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
                    >
                      Choose File
                    </button>
                    <button
                      onClick={generateSampleExcel}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Download Template
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">File Format Requirements</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700 mb-2 font-semibold">Required Columns:</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 mb-4">
                  <li><strong>Keyword</strong> - The keyword text</li>
                  <li><strong>Volume</strong> - Any number (0 or greater)</li>
                  <li><strong>At least ONE of:</strong> Product Name, Brand, or Sub-Brand</li>
                </ul>
                <p className="text-sm text-gray-700 mb-2 font-semibold">Optional Grouping Columns:</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 mb-4">
                  <li><strong>Product Name</strong> - Name of the product</li>
                  <li><strong>Brand</strong> - Brand name (enables Brand view)</li>
                  <li><strong>Sub-Brand</strong> - Sub-brand name (enables Sub-Brand view)</li>
                </ul>
                <p className="text-sm text-gray-700 mb-2 font-semibold">Other Optional Columns:</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li><strong>Product Category</strong> - Category of the product</li>
                  <li><strong>Intent Category</strong> - Any category name (defaults to "General")</li>
                  <li><strong>Ingredients</strong> - Product ingredients</li>
                  <li><strong>Legal Claims</strong> - Information about legal claims for the product</li>
                  <li><strong>Copy</strong> - Product copy/description text</li>
                  <li><strong>ds_filter_passed</strong> - TRUE or FALSE to indicate filter status</li>
                  <li><strong>research_type</strong> - Research type classification (e.g., "attribute keyword", "search keyword")</li>
                  <li><strong>amazon</strong> - TRUE or FALSE to indicate Amazon relevance</li>
                  <li><strong>target</strong> - TRUE or FALSE to indicate Target relevance</li>
                  <li><strong>walmart</strong> - TRUE or FALSE to indicate Walmart relevance</li>
                  <li><strong>kroger</strong> - TRUE or FALSE to indicate Kroger relevance</li>
                  <li><strong>instacart</strong> - TRUE or FALSE to indicate Instacart relevance</li>
                  <li><strong>intent_score</strong> - Number between 1-100 indicating how well keyword fits shopper intent</li>
                  <li><strong>keyword_eng</strong> - English translation of the keyword</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">Note: Column names are case-insensitive and ignore spaces</p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Preview Import Data</h3>
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h4 className="text-red-800 font-semibold mb-2">Validation Errors:</h4>
                  <div className="max-h-40 overflow-y-auto">
                    {validationErrors.map((error, index) => (
                      <p key={index} className="text-red-700 text-sm">
                        Row {error.row}, {error.column}: {error.message}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {previewData.map((product, index) => {
                const displayName = product.productName || product.brand || product.subBrand || 'Unnamed';
                const displayDetails = [
                  product.productName && `Product: ${product.productName}`,
                  product.brand && `Brand: ${product.brand}`,
                  product.subBrand && `Sub-Brand: ${product.subBrand}`,
                ].filter(Boolean).join(' | ');
                
                return (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-primary-900">{displayName}</h4>
                  <p className="text-xs text-gray-500 mb-2">
                    {displayDetails} | Category: {product.productCategory} | Keywords: {product.keywords.length}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {product.keywords.slice(0, 10).map((keyword, kIndex) => {
                      const keywordColor = intentCategoryConfig[keyword.intent]?.color || getIntentColor(keyword.intent);
                      return (
                        <span
                          key={kIndex}
                          className={`px-2 py-1 text-xs rounded border ${keywordColor} flex flex-col items-center`}
                        >
                          <span>{keyword.text}</span>
                          {keyword.keyword_eng && (
                            <span className="italic text-gray-600 text-[10px]">{keyword.keyword_eng}</span>
                          )}
                          <span className="text-[10px] opacity-75">({keyword.volume})</span>
                        </span>
                      );
                    })}
                    {product.keywords.length > 10 && (
                      <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
                        +{product.keywords.length - 10} more
                      </span>
                    )}
                  </div>
                </div>
              );
              })}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={validationErrors.length > 0}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  validationErrors.length > 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                Import Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
